import {
  collection,
  query,
  getDocs,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

const METRICS_COLLECTION = 'metrics';

/**
 * Metrics Service
 *
 * Queries the pre-computed metrics collection for analytics data.
 */
class MetricsService {
  /**
   * Fetch metrics for a date range and optional store filter
   *
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {string|null} storeId - Store ID or null/'all' for all stores
   * @returns {Promise<Array>} Array of metrics documents sorted by date
   */
  async fetchMetrics(startDate, endDate, storeId = 'all') {
    const metricsRef = collection(db, METRICS_COLLECTION);

    // Build query constraints
    const queryStoreId = storeId === 'all' || !storeId ? 'all' : storeId;

    const q = query(
      metricsRef,
      where('store_id', '==', queryStoreId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );

    const snapshot = await getDocs(q);
    const metrics = [];

    snapshot.forEach((doc) => {
      metrics.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return metrics;
  }

  /**
   * Get date range presets
   */
  getDateRangePresets() {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    // 7 days ago
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);

    // 30 days ago
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 29);

    // 90 days ago
    const ninetyDaysAgo = new Date(today);
    ninetyDaysAgo.setDate(today.getDate() - 89);

    // 1 year ago
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    return {
      '7days': {
        label: '7D',
        startDate: formatDate(sevenDaysAgo),
        endDate: formatDate(today),
      },
      '30days': {
        label: '30D',
        startDate: formatDate(thirtyDaysAgo),
        endDate: formatDate(today),
      },
      '90days': {
        label: '90D',
        startDate: formatDate(ninetyDaysAgo),
        endDate: formatDate(today),
      },
      '1year': {
        label: '1Y',
        startDate: formatDate(oneYearAgo),
        endDate: formatDate(today),
      },
      'all': {
        label: 'Todo',
        startDate: '2023-01-01', // Beginning of historical data
        endDate: formatDate(today),
      },
    };
  }

  /**
   * Get the equivalent previous period for comparison
   * E.g., if range is 7 days, returns the 7 days before that
   */
  getPreviousPeriodRange(startDate, endDate) {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    // Calculate period length in days
    const periodLength = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    // Previous period ends the day before current start
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);

    // Previous period starts periodLength days before that
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - periodLength + 1);

    return {
      startDate: prevStart.toISOString().split('T')[0],
      endDate: prevEnd.toISOString().split('T')[0],
      periodLength,
    };
  }

  /**
   * Fetch metrics with period-over-period comparison
   */
  async fetchMetricsWithComparison(startDate, endDate, storeId = 'all') {
    // Get previous period range
    const previousRange = this.getPreviousPeriodRange(startDate, endDate);

    // Fetch both periods in parallel
    const [currentMetrics, previousMetrics] = await Promise.all([
      this.fetchMetrics(startDate, endDate, storeId),
      this.fetchMetrics(previousRange.startDate, previousRange.endDate, storeId),
    ]);

    const currentData = this.transformForCharts(currentMetrics);
    const previousData = this.transformForCharts(previousMetrics);

    return {
      current: currentData,
      previous: previousData,
      previousRange,
    };
  }

  /**
   * Get the same date range from the previous year
   */
  getPreviousYearRange(startDate, endDate) {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    start.setFullYear(start.getFullYear() - 1);
    end.setFullYear(end.getFullYear() - 1);

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }

  /**
   * Fetch metrics with year-over-year comparison
   */
  async fetchMetricsWithYoY(startDate, endDate, storeId = 'all') {
    // Fetch current period
    const currentMetrics = await this.fetchMetrics(startDate, endDate, storeId);

    // Fetch same period last year
    const lastYearRange = this.getPreviousYearRange(startDate, endDate);
    const lastYearMetrics = await this.fetchMetrics(
      lastYearRange.startDate,
      lastYearRange.endDate,
      storeId
    );

    return {
      current: currentMetrics,
      lastYear: lastYearMetrics,
      lastYearRange,
    };
  }

  /**
   * Helper to get a nested value from an object that may have dot-notation keys
   * Handles both nested objects (orders.cakes.total) and flat keys ("orders.cakes.total")
   */
  getNestedValue(obj, path, defaultValue = 0) {
    // First try the flat key (e.g., "orders.cakes.total")
    if (obj[path] !== undefined) {
      return obj[path];
    }
    // Then try nested path
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
      if (current === undefined || current === null) {
        return defaultValue;
      }
      current = current[key];
    }
    return current !== undefined ? current : defaultValue;
  }

  /**
   * Transform metrics data for chart display
   * Returns data suitable for Recharts LineChart
   *
   * @param {Array} metrics - Raw metrics documents
   * @returns {Object} Transformed data with chart series
   */
  transformForCharts(metrics) {
    // Sort by date
    const sorted = [...metrics].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    // Determine date format based on data length
    const dateFormat = this.getDateFormat(sorted.length);

    // Build chart data points
    const chartData = sorted.map((m) => {
      // Helper to get values - handles both nested and flat dot-notation keys
      const get = (path) => this.getNestedValue(m, path, 0);

      const cakesTotal = get('orders.cakes.total');
      const roscasItemCount = get('orders.roscas.itemCount');
      const roscasTotal = get('orders.roscas.total');
      const merchantTotal = get('orders.merchantOrders.total');
      const customTotal = get('orders.customOrders.total');
      const breadsTotal = get('orders.breads.total');

      // Channel data
      const onlineTotal = get('channels.online.total');
      const inpersonTotal = get('channels.inperson.total');

      return {
        date: m.date,
        displayDate: this.formatDisplayDate(m.date, dateFormat),
        // Totals - using units sold for roscas, order counts for others
        cakesTotal,
        roscasTotal: roscasItemCount || roscasTotal, // Units sold, not orders
        roscasOrders: roscasTotal, // Keep order count available if needed
        merchantTotal,
        customTotal,
        breadsTotal,
        // Combined total (units for roscas, orders for others)
        ordersTotal:
          cakesTotal +
          (roscasItemCount || roscasTotal) +
          merchantTotal +
          customTotal +
          breadsTotal,
        // Channel breakdowns
        onlineTotal,
        inpersonTotal,
        // Status breakdowns
        cakesReady: get('orders.cakes.ready'),
        cakesPending: get('orders.cakes.pending'),
        cakesPrePaid: get('orders.cakes.prePaid'),
        roscasPaid: get('orders.roscas.paid'),
        roscasPickedUp: get('orders.roscas.pickedUp'),
        merchantRevenue: get('orders.merchantOrders.totalRevenue'),
        customDelivered: get('orders.customOrders.delivered'),
        breadsPending: get('orders.breads.pending'),
        breadsBaked: get('orders.breads.baked'),
        breadsCompleted: get('orders.breads.completed'),
      };
    });

    // Calculate summary statistics
    const summary = this.calculateSummary(chartData);

    // Calculate optimal tick interval for x-axis
    const tickInterval = this.getTickInterval(chartData.length);

    return {
      chartData,
      summary,
      tickInterval,
    };
  }

  /**
   * Calculate optimal tick interval based on data points
   * Returns number of items to skip between ticks
   */
  getTickInterval(dataLength) {
    if (dataLength <= 7) return 0;           // Show all
    if (dataLength <= 14) return 1;          // Every other
    if (dataLength <= 30) return 4;          // ~Weekly
    if (dataLength <= 90) return 13;         // ~Bi-weekly
    if (dataLength <= 180) return 29;        // ~Monthly
    return Math.floor(dataLength / 6) - 1;   // ~6 ticks total
  }

  /**
   * Transform metrics data with YoY comparison for chart display
   * Aligns last year's data to current year dates for overlay
   */
  transformForChartsWithYoY(currentMetrics, lastYearMetrics) {
    const currentData = this.transformForCharts(currentMetrics);
    const lastYearData = this.transformForCharts(lastYearMetrics);

    // Create a map of last year data by day-month (ignoring year)
    const lastYearByDayMonth = {};
    lastYearData.chartData.forEach((item) => {
      // Extract MM-DD from date
      const dayMonth = item.date.slice(5); // "MM-DD"
      lastYearByDayMonth[dayMonth] = item;
    });

    // Merge last year data into current data
    const chartDataWithYoY = currentData.chartData.map((item) => {
      const dayMonth = item.date.slice(5);
      const lastYearItem = lastYearByDayMonth[dayMonth] || {};

      return {
        ...item,
        // Last year totals (prefixed with 'ly')
        lyCakesTotal: lastYearItem.cakesTotal || 0,
        lyRoscasTotal: lastYearItem.roscasTotal || 0,
        lyBreadsTotal: lastYearItem.breadsTotal || 0,
        lyMerchantTotal: lastYearItem.merchantTotal || 0,
        lyCustomTotal: lastYearItem.customTotal || 0,
        lyOrdersTotal: lastYearItem.ordersTotal || 0,
      };
    });

    return {
      chartData: chartDataWithYoY,
      summary: currentData.summary,
      lastYearSummary: lastYearData.summary,
    };
  }

  /**
   * Calculate summary statistics from chart data
   */
  calculateSummary(chartData) {
    if (chartData.length === 0) {
      return {
        totalOrders: 0,
        totalCakes: 0,
        totalRoscas: 0,
        totalMerchant: 0,
        totalCustom: 0,
        totalBreads: 0,
        totalRevenue: 0,
        totalOnline: 0,
        totalInPerson: 0,
        avgOrdersPerDay: 0,
      };
    }

    const totals = chartData.reduce(
      (acc, day) => ({
        totalOrders: acc.totalOrders + day.ordersTotal,
        totalCakes: acc.totalCakes + day.cakesTotal,
        totalRoscas: acc.totalRoscas + day.roscasTotal,
        totalMerchant: acc.totalMerchant + day.merchantTotal,
        totalCustom: acc.totalCustom + day.customTotal,
        totalBreads: acc.totalBreads + day.breadsTotal,
        totalRevenue: acc.totalRevenue + day.merchantRevenue,
        totalOnline: acc.totalOnline + (day.onlineTotal || 0),
        totalInPerson: acc.totalInPerson + (day.inpersonTotal || 0),
      }),
      {
        totalOrders: 0,
        totalCakes: 0,
        totalRoscas: 0,
        totalMerchant: 0,
        totalCustom: 0,
        totalBreads: 0,
        totalRevenue: 0,
        totalOnline: 0,
        totalInPerson: 0,
      }
    );

    return {
      ...totals,
      avgOrdersPerDay: Math.round(totals.totalOrders / chartData.length),
    };
  }

  /**
   * Format date for display in charts
   * Adapts format based on date range context
   *
   * @param {string} dateStr - Date string in YYYY-MM-DD format
   * @param {string} format - Format type ('dayName', 'monthDay', 'monthOnly', 'default')
   * @param {boolean} forceYear - Always show year regardless of current year
   */
  formatDisplayDate(dateStr, format = 'default', forceYear = false) {
    const date = new Date(dateStr + 'T00:00:00');
    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();
    const showYear = forceYear || dateYear !== currentYear;

    const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    switch (format) {
      case 'dayName':
        // "Lun 13"
        return `${dayNames[date.getDay()]} ${date.getDate()}`;
      case 'monthDay':
        // "13 ene" or "13 ene 24"
        if (showYear) {
          return date.toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'short',
            year: '2-digit',
          });
        }
        return date.toLocaleDateString('es-MX', {
          day: 'numeric',
          month: 'short',
        });
      case 'monthOnly':
        // "ene 24" (always show year for multi-year views)
        return date.toLocaleDateString('es-MX', {
          month: 'short',
          year: '2-digit',
        });
      default:
        // Full format with year if needed
        if (showYear) {
          return date.toLocaleDateString('es-MX', {
            month: 'short',
            day: 'numeric',
            year: '2-digit',
          });
        }
        return date.toLocaleDateString('es-MX', {
          month: 'short',
          day: 'numeric',
        });
    }
  }

  /**
   * Determine the best date format based on data length
   */
  getDateFormat(dataLength) {
    if (dataLength <= 14) return 'dayName';      // 7D, 14D: "Lun 13"
    if (dataLength <= 60) return 'monthDay';     // 30D, 60D: "13 ene"
    return 'monthOnly';                          // 90D+: "ene"
  }

  /**
   * Get week boundaries (Monday-Sunday)
   */
  getWeekBoundaries(referenceDate = new Date()) {
    const formatDate = (date) => date.toISOString().split('T')[0];
    const date = new Date(referenceDate);

    // Get Monday of current week
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));
    monday.setHours(0, 0, 0, 0);

    // Get Sunday of current week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return {
      start: formatDate(monday),
      end: formatDate(sunday),
    };
  }

  /**
   * Fetch week-over-week comparison data
   * Compares current week (Mon-Sun) with previous week
   */
  async fetchWeekComparison(storeId = 'all') {
    const today = new Date();
    const thisWeek = this.getWeekBoundaries(today);

    // Get last week
    const lastWeekDate = new Date(today);
    lastWeekDate.setDate(lastWeekDate.getDate() - 7);
    const lastWeek = this.getWeekBoundaries(lastWeekDate);

    // Fetch both weeks in parallel
    const [thisWeekMetrics, lastWeekMetrics] = await Promise.all([
      this.fetchMetrics(thisWeek.start, thisWeek.end, storeId),
      this.fetchMetrics(lastWeek.start, lastWeek.end, storeId),
    ]);

    const thisWeekData = this.transformForCharts(thisWeekMetrics);
    const lastWeekData = this.transformForCharts(lastWeekMetrics);

    // Calculate percentage changes
    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return Math.round(((current - previous) / previous) * 100);
    };

    return {
      thisWeek: {
        range: thisWeek,
        summary: thisWeekData.summary,
      },
      lastWeek: {
        range: lastWeek,
        summary: lastWeekData.summary,
      },
      changes: {
        totalOrders: calculateChange(
          thisWeekData.summary.totalOrders,
          lastWeekData.summary.totalOrders
        ),
        totalCakes: calculateChange(
          thisWeekData.summary.totalCakes,
          lastWeekData.summary.totalCakes
        ),
        totalRoscas: calculateChange(
          thisWeekData.summary.totalRoscas,
          lastWeekData.summary.totalRoscas
        ),
        totalBreads: calculateChange(
          thisWeekData.summary.totalBreads,
          lastWeekData.summary.totalBreads
        ),
        avgOrdersPerDay: calculateChange(
          thisWeekData.summary.avgOrdersPerDay,
          lastWeekData.summary.avgOrdersPerDay
        ),
      },
    };
  }

  /**
   * Calculate day-of-week averages from metrics data
   * Groups data by weekday and calculates average orders
   */
  calculateDayOfWeekAverages(chartData) {
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const dayFullNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

    // Initialize accumulators for each day
    const dayStats = Array(7).fill(null).map(() => ({
      totalOrders: 0,
      totalCakes: 0,
      totalRoscas: 0,
      totalBreads: 0,
      count: 0,
    }));

    // Accumulate data by day of week
    chartData.forEach((item) => {
      const date = new Date(item.date + 'T00:00:00');
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

      dayStats[dayOfWeek].totalOrders += item.ordersTotal;
      dayStats[dayOfWeek].totalCakes += item.cakesTotal;
      dayStats[dayOfWeek].totalRoscas += item.roscasTotal;
      dayStats[dayOfWeek].totalBreads += item.breadsTotal;
      dayStats[dayOfWeek].count += 1;
    });

    // Calculate averages and format for chart
    // Reorder to start with Monday (1, 2, 3, 4, 5, 6, 0)
    const mondayFirst = [1, 2, 3, 4, 5, 6, 0];

    const result = mondayFirst.map((dayIndex) => {
      const stats = dayStats[dayIndex];
      const count = stats.count || 1; // Avoid division by zero

      return {
        day: dayNames[dayIndex],
        dayFull: dayFullNames[dayIndex],
        dayIndex,
        avgOrders: Math.round(stats.totalOrders / count),
        avgCakes: Math.round((stats.totalCakes / count) * 10) / 10,
        avgRoscas: Math.round((stats.totalRoscas / count) * 10) / 10,
        avgBreads: Math.round((stats.totalBreads / count) * 10) / 10,
        totalOrders: stats.totalOrders,
        weeksCount: stats.count,
      };
    });

    // Find busiest day
    const maxOrders = Math.max(...result.map((d) => d.avgOrders));
    result.forEach((d) => {
      d.isBusiest = d.avgOrders === maxOrders && maxOrders > 0;
    });

    return result;
  }

  /**
   * Calculate product mix percentages
   * Returns distribution of orders by product type
   */
  calculateProductMix(summary) {
    const total = summary.totalOrders || 0;

    if (total === 0) {
      return [
        { name: 'Pasteles', value: 0, percentage: 0, color: '#c43b21' },
        { name: 'Roscas', value: 0, percentage: 0, color: '#7b1fa2' },
        { name: 'Panes', value: 0, percentage: 0, color: '#d4a574' },
        { name: 'Especiales', value: 0, percentage: 0, color: '#c98700' },
        { name: 'Merchant', value: 0, percentage: 0, color: '#0d8742' },
      ];
    }

    const mix = [
      {
        name: 'Pasteles',
        value: summary.totalCakes,
        percentage: Math.round((summary.totalCakes / total) * 100),
        color: '#c43b21',
      },
      {
        name: 'Roscas',
        value: summary.totalRoscas,
        percentage: Math.round((summary.totalRoscas / total) * 100),
        color: '#7b1fa2',
      },
      {
        name: 'Panes',
        value: summary.totalBreads,
        percentage: Math.round((summary.totalBreads / total) * 100),
        color: '#d4a574',
      },
      {
        name: 'Especiales',
        value: summary.totalCustom,
        percentage: Math.round((summary.totalCustom / total) * 100),
        color: '#c98700',
      },
      {
        name: 'Merchant',
        value: summary.totalMerchant,
        percentage: Math.round((summary.totalMerchant / total) * 100),
        color: '#0d8742',
      },
    ];

    // Filter out zero values and sort by value descending
    return mix
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }

  /**
   * Calculate channel mix percentages
   * Returns distribution of orders by channel (online vs in-person)
   */
  calculateChannelMix(summary) {
    const total = summary.totalOrders || 0;

    if (total === 0) {
      return [];
    }

    const mix = [
      {
        name: 'En Línea',
        value: summary.totalOnline || 0,
        percentage: Math.round(((summary.totalOnline || 0) / total) * 100),
        color: '#2196F3', // Blue for online
      },
      {
        name: 'En Tienda',
        value: summary.totalInPerson || 0,
        percentage: Math.round(((summary.totalInPerson || 0) / total) * 100),
        color: '#4CAF50', // Green for in-person
      },
    ];

    // Filter out zero values and sort by value descending
    return mix
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }

  /**
   * Get extended date range presets including week-based options
   */
  getExtendedDateRangePresets() {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    // This week (Mon-Sun)
    const thisWeek = this.getWeekBoundaries(today);

    // Last week
    const lastWeekDate = new Date(today);
    lastWeekDate.setDate(lastWeekDate.getDate() - 7);
    const lastWeek = this.getWeekBoundaries(lastWeekDate);

    // This month
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    // Last month
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);

    // This quarter
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);

    // This year
    const thisYearStart = new Date(today.getFullYear(), 0, 1);

    return {
      thisWeek: {
        label: 'Esta Semana',
        shortLabel: 'Sem',
        startDate: thisWeek.start,
        endDate: thisWeek.end,
      },
      lastWeek: {
        label: 'Semana Pasada',
        shortLabel: 'Sem-1',
        startDate: lastWeek.start,
        endDate: lastWeek.end,
      },
      '7days': {
        label: 'Últimos 7 días',
        shortLabel: '7D',
        startDate: formatDate(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)),
        endDate: formatDate(today),
      },
      '30days': {
        label: 'Últimos 30 días',
        shortLabel: '30D',
        startDate: formatDate(new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)),
        endDate: formatDate(today),
      },
      thisMonth: {
        label: 'Este Mes',
        shortLabel: 'Mes',
        startDate: formatDate(thisMonthStart),
        endDate: formatDate(today),
      },
      lastMonth: {
        label: 'Mes Pasado',
        shortLabel: 'Mes-1',
        startDate: formatDate(lastMonthStart),
        endDate: formatDate(lastMonthEnd),
      },
      thisQuarter: {
        label: 'Este Trimestre',
        shortLabel: 'Trim',
        startDate: formatDate(thisQuarterStart),
        endDate: formatDate(today),
      },
      thisYear: {
        label: 'Este Año',
        shortLabel: 'Año',
        startDate: formatDate(thisYearStart),
        endDate: formatDate(today),
      },
    };
  }
}

const metricsService = new MetricsService();
export default metricsService;
